import React, { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PaywallPrompt } from '../PaywallPrompt';
import { resolvePaywallDestination } from '../resolvePaywallDestination';

import type { PaywallModalProps } from './PaywallModal.types';
import type { PaymentRequiredError } from '@personal-trainer-app/shared';

import { Modal } from '@/components/ui/Modal';

/**
 * App-level paywall modal. Rendered once by `PaywallProvider` (never per
 * surface) and driven by the provider's paywall-error state: a `402` thrown
 * from any mutation handler opens it via `usePaywall().showPaywall(err)`.
 *
 * Wraps the shared `Modal` around `PaywallPrompt variant="modal"` so the
 * backend-authored upgrade copy renders identically to the read-path card
 * (`PaywallBoundary`). The CTA resolves the internal upgrade route
 * (`resolvePaywallDestination`, honor-but-coalesce) and navigates client-side.
 * Dialog chrome (backdrop, escape-to-dismiss) is owned by `Modal`. It has no
 * header title, so the dialog is named by the prompt's own heading.
 */
export function PaywallModal({
  error,
  onClose,
}: PaywallModalProps): React.JSX.Element {
  const navigate = useNavigate();
  const titleId = useId();

  // Retain the last error so the prompt stays rendered through the Modal's
  // ~200ms exit animation — reading `error` directly would blank the body
  // mid-fade once the provider clears it (mirrors ChangePlanModal's
  // "don't reset on close" guard).
  const [rendered, setRendered] = useState<PaymentRequiredError | null>(error);
  useEffect(() => {
    if (error) setRendered(error);
  }, [error]);

  const handleUpgrade = (): void => {
    if (!rendered) return;
    const destination = resolvePaywallDestination(rendered);
    // Close first so the modal isn't mid-animation over the destination, then
    // navigate client-side (the provider is inside the router).
    onClose();
    navigate(destination);
  };

  return (
    <Modal
      isOpen={error !== null}
      onClose={onClose}
      size="sm"
      aria-labelledby={titleId}
    >
      {rendered && (
        <PaywallPrompt
          error={rendered}
          variant="modal"
          titleId={titleId}
          onUpgrade={handleUpgrade}
          data-testid="paywall-modal-prompt"
        />
      )}
    </Modal>
  );
}

export default PaywallModal;
